import React, { useEffect, useCallback, useMemo } from "react";
import { X, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|svg|bmp|ico|avif)$/i;

export const isImageFile = (file) => {
    if (!file) return false;
    if (
        file.type &&
        typeof file.type === "string" &&
        file.type.startsWith("image/")
    ) {
        return true;
    }
    const filename = file.name || file.originalname || "";
    const key = file.key || "";
    const cleanUrl = String(file.url || file.source_url || "").split("?")[0];
    return (
        IMAGE_EXTENSIONS.test(filename) ||
        IMAGE_EXTENSIONS.test(key) ||
        IMAGE_EXTENSIONS.test(cleanUrl)
    );
};

export const getFileName = (file) =>
    file?.name ||
    file?.originalname ||
    file?.key?.split("/").pop() ||
    "Attachment";

const FilePreviewModal = ({
    file,
    files = [],
    currentIndex = 0,
    onClose,
    onNavigate,
}) => {
    const activeFile = file || files[currentIndex];

    // Filter to navigable images when files array is provided
    const imageIndices = useMemo(() => {
        if (!files.length) return [];
        return files
            .map((f, idx) => (isImageFile(f) ? idx : null))
            .filter((idx) => idx !== null);
    }, [files]);

    const activeImagePos = imageIndices.indexOf(currentIndex);

    const handlePrev = useCallback(() => {
        if (!onNavigate || imageIndices.length <= 1) return;
        const prevPos =
            (activeImagePos - 1 + imageIndices.length) % imageIndices.length;
        onNavigate(imageIndices[prevPos]);
    }, [onNavigate, imageIndices, activeImagePos]);

    const handleNext = useCallback(() => {
        if (!onNavigate || imageIndices.length <= 1) return;
        const nextPos = (activeImagePos + 1) % imageIndices.length;
        onNavigate(imageIndices[nextPos]);
    }, [onNavigate, imageIndices, activeImagePos]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose?.();
            } else if (e.key === "ArrowLeft") {
                handlePrev();
            } else if (e.key === "ArrowRight") {
                handleNext();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose, handlePrev, handleNext]);

    if (!activeFile) return null;

    const fileName = getFileName(activeFile);
    const fileUrl = activeFile.url || activeFile.source_url;
    const isImg = isImageFile(activeFile);

    return (
        <div
            className="epm-step-lightbox fixed inset-0 z-[100] flex flex-col items-center justify-center p-4 sm:p-8 cursor-default select-none bg-black/85 backdrop-blur-sm"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="File preview"
        >
            {/* Stable fixed-width container with no card background */}
            <div
                className="relative flex flex-col items-center w-[92vw] max-w-5xl h-[88vh]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Viewing area: fixed dimensions, no card background */}
                <div className="relative flex-1 min-h-0 w-full flex items-center justify-center">
                    {imageIndices.length > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePrev();
                                }}
                                className="absolute left-0 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/50 hover:bg-black/80 text-white transition-colors border-0 cursor-pointer z-10 flex items-center justify-center"
                                title="Previous image (Left Arrow)"
                            >
                                <ChevronLeft size={24} />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleNext();
                                }}
                                className="absolute right-0 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/50 hover:bg-black/80 text-white transition-colors border-0 cursor-pointer z-10 flex items-center justify-center"
                                title="Next image (Right Arrow)"
                            >
                                <ChevronRight size={24} />
                            </button>
                        </>
                    )}

                    <div className="flex flex-col items-center justify-center w-full h-full">
                        <div className="w-full flex items-center justify-between gap-4 mb-3 text-white shrink-0 px-10">
                            <div className="flex items-center gap-2.5 min-w-0">
                                <span
                                    className="text-sm font-medium truncate max-w-[55vw]"
                                    title={fileName}
                                >
                                    {fileName}
                                </span>
                                {imageIndices.length > 1 && (
                                    <span className="text-xs text-white/60 shrink-0 font-normal">
                                        ({activeImagePos + 1} of{" "}
                                        {imageIndices.length})
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2.5 shrink-0">
                                {fileUrl && (
                                    <a
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs font-medium transition-colors no-underline"
                                        title="Open original in new tab"
                                    >
                                        <ExternalLink size={13} />
                                        <span>Open original</span>
                                    </a>
                                )}
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="p-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white transition-colors border-0 cursor-pointer flex items-center"
                                    title="Close (Esc)"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>
                        {isImg ? (
                            <img
                                src={fileUrl}
                                alt={fileName}
                                className="max-h-full max-w-[calc(100%-80px)] object-contain rounded-lg shadow-2xl"
                            />
                        ) : (
                            <div className="p-8 rounded-lg text-center text-white bg-white/10">
                                <p className="text-sm mb-4 text-white/80">
                                    Preview not available for this file type
                                </p>
                                {fileUrl && (
                                    <a
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="px-4 py-2 bg-[var(--primary)] text-white text-xs font-semibold rounded-md no-underline inline-block hover:opacity-90 transition-opacity"
                                    >
                                        Download / View file
                                    </a>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilePreviewModal;
